import { t } from "@lingui/macro";
import type {
	CalendarToolState,
	GetGoogleCalendarToolSetupResponse,
} from "@repo/common";
import {
	configureGoogleCalendarTool,
	disconnectGoogleCalendarTool,
	getGoogleCalendarToolSetup,
	startGoogleCalendarOAuth,
} from "@repo/ui";
import {
	AlertCircle,
	CalendarDays,
	CheckCircle2,
	ExternalLink,
	Loader2,
	RefreshCw,
	Unlink,
} from "lucide-react";
import React from "react";
import { toast } from "sonner";
import type { Agent } from "../types";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { ToolLoadingCard } from "./ToolLoadingCard";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

interface GoogleCalendarSettingsCardProps {
	companyId: string;
	agents: Agent[];
}

function validTimeZone(timeZone?: string): boolean {
	if (!timeZone) return false;
	try {
		new Intl.DateTimeFormat("en", { timeZone }).format();
		return true;
	} catch {
		return false;
	}
}

function stateLabel(state: CalendarToolState): string {
	switch (state) {
		case "not_available":
			return t({ id: "calendarToolNotAvailable", message: "Not available" });
		case "available_not_configured":
			return t({
				id: "calendarToolNotConfigured",
				message: "Available, not configured",
			});
		case "available_configured":
			return t({ id: "calendarToolConfigured", message: "Configured" });
		case "configuration_error":
			return t({
				id: "calendarToolConfigurationError",
				message: "Configuration error",
			});
	}
}

function errorMessage(error?: string | null): string | null {
	if (!error) return null;
	if (error === "agent_timezone_missing_or_invalid") {
		return t({
			id: "calendarAgentTimezoneError",
			message: "Set a valid timezone for this agent before enabling Calendar.",
		});
	}
	if (error === "oauth_completion_failed") {
		return t({
			id: "calendarOAuthCompletionError",
			message: "Google authorization failed. Reconnect the calendar account.",
		});
	}
	if (error === "token_revoked" || error === "oauth_token_revoked") {
		return t({
			id: "calendarTokenRevokedError",
			message:
				"Google Calendar access was revoked or expired. Reconnect your account.",
		});
	}
	return t({
		id: "calendarUnknownConfigurationError",
		message: "Calendar configuration needs attention. Reconnect and try again.",
	});
}

function getProblemMessage(
	setup: GetGoogleCalendarToolSetupResponse,
): string | null {
	if (setup.configurationError) {
		return errorMessage(setup.configurationError);
	}
	const invalidBinding = setup.bindings.find(
		(binding) => binding.enabled && binding.error,
	);
	if (invalidBinding) {
		return (
			errorMessage(invalidBinding.error) ||
			t({
				id: "calendarAgentBindingIssues",
				message:
					"One or more enabled agents have configuration errors (e.g. missing timezone).",
			})
		);
	}
	if (setup.state === "configuration_error") {
		return t({
			id: "calendarUnknownConfigurationError",
			message:
				"Calendar configuration needs attention. Reconnect and try again.",
		});
	}
	return null;
}

export function GoogleCalendarSettingsCard({
	companyId,
	agents,
}: GoogleCalendarSettingsCardProps) {
	const [setup, setSetup] =
		React.useState<GetGoogleCalendarToolSetupResponse>();
	const [selectedCalendarId, setSelectedCalendarId] = React.useState("");
	const [selectedAgentIds, setSelectedAgentIds] = React.useState<Set<string>>(
		new Set(),
	);
	const [isLoading, setIsLoading] = React.useState(true);
	const [action, setAction] = React.useState<
		"connect" | "save" | "disconnect" | null
	>(null);
	const [showDisconnectDialog, setShowDisconnectDialog] = React.useState(false);
	const oauthPollRef = React.useRef<number | null>(null);

	const handleDisconnect = async () => {
		setAction("disconnect");
		try {
			await disconnectGoogleCalendarTool({ companyId });
			toast.success(
				t({
					id: "calendarDisconnected",
					message: "Google Calendar disconnected.",
				}),
			);
			setShowDisconnectDialog(false);
			await loadSetup(false);
		} catch (error) {
			console.error("Failed to disconnect Google Calendar", error);
			toast.error(
				t({
					id: "calendarDisconnectFailed",
					message: "Could not disconnect Google Calendar.",
				}),
			);
		} finally {
			setAction(null);
		}
	};

	const loadSetup = React.useCallback(
		async (showError = true) => {
			setIsLoading(true);
			try {
				const result = await getGoogleCalendarToolSetup({ companyId });
				const nextSetup = result.data;
				setSetup(nextSetup);
				setSelectedCalendarId(
					nextSetup.defaultCalendarId ||
						nextSetup.calendars.find((calendar) => calendar.primary)?.id ||
						nextSetup.calendars[0]?.id ||
						"",
				);
				setSelectedAgentIds(
					new Set(
						nextSetup.bindings
							.filter((binding) => binding.enabled)
							.map((binding) => binding.agentId),
					),
				);
			} catch (error) {
				console.error("Failed to load Google Calendar configuration", error);
				if (showError) {
					toast.error(
						t({
							id: "calendarConfigurationLoadFailed",
							message: "Could not load the Calendar configuration.",
						}),
					);
				}
			} finally {
				setIsLoading(false);
			}
		},
		[companyId],
	);

	React.useEffect(() => {
		void loadSetup();
	}, [loadSetup]);

	React.useEffect(
		() => () => {
			if (oauthPollRef.current !== null) {
				window.clearInterval(oauthPollRef.current);
				oauthPollRef.current = null;
			}
		},
		[],
	);

	const handleConnect = async () => {
		const popup = window.open(
			"about:blank",
			"provoize-google-calendar",
			"popup,width=600,height=720",
		);
		if (!popup) {
			toast.error(
				t({
					id: "calendarPopupBlocked",
					message: "Allow popups to connect Google Calendar.",
				}),
			);
			return;
		}

		setAction("connect");
		try {
			const result = await startGoogleCalendarOAuth({ companyId });
			popup.location.replace(result.data.authorizationUrl);
			const deadline = Date.now() + 5 * 60 * 1000;
			let pollInFlight = false;
			const pollId = window.setInterval(() => {
				if (pollInFlight || oauthPollRef.current !== pollId) return;
				if (Date.now() >= deadline) {
					window.clearInterval(pollId);
					oauthPollRef.current = null;
					setAction(null);
					void loadSetup(false);
					return;
				}
				pollInFlight = true;
				void getGoogleCalendarToolSetup({ companyId })
					.then((setupResult) => {
						if (
							oauthPollRef.current !== pollId ||
							setupResult.data.lastOAuthAttemptId !== result.data.attemptId
						)
							return;
						window.clearInterval(pollId);
						oauthPollRef.current = null;
						setAction(null);
						void loadSetup(false);
					})
					.catch(() => {
						// A transient setup read can be retried on the next poll.
					})
					.finally(() => {
						pollInFlight = false;
					});
			}, 2000);
			oauthPollRef.current = pollId;
			toast.success(
				t({
					id: "calendarCompleteGoogleAuthorization",
					message: "Complete Google authorization in the new window.",
				}),
			);
		} catch (error) {
			popup.close();
			setAction(null);
			console.error("Failed to start Google Calendar authorization", error);
			toast.error(
				t({
					id: "calendarOAuthStartFailed",
					message: "Could not start Google Calendar authorization.",
				}),
			);
		}
	};

	const handleSave = async () => {
		if (!selectedCalendarId) return;
		setAction("save");
		try {
			await configureGoogleCalendarTool({
				companyId,
				calendarId: selectedCalendarId,
				agentIds: [...selectedAgentIds],
			});
			await loadSetup(false);
			toast.success(
				t({
					id: "calendarConfigurationSaved",
					message: "Calendar configuration saved.",
				}),
			);
		} catch (error) {
			console.error("Failed to save Google Calendar configuration", error);
			toast.error(
				t({
					id: "calendarConfigurationSaveFailed",
					message: "Could not save the Calendar configuration.",
				}),
			);
		} finally {
			setAction(null);
		}
	};

	const toggleAgent = (agentId: string, enabled: boolean) => {
		setSelectedAgentIds((current) => {
			const next = new Set(current);
			if (enabled) next.add(agentId);
			else next.delete(agentId);
			return next;
		});
	};

	const isConnected =
		(setup?.calendars && setup.calendars.length > 0) ||
		Boolean(setup?.defaultCalendarId);
	const hasProblem =
		setup?.state === "configuration_error" ||
		Boolean(setup?.configurationError);
	const isConnectionValid = isConnected && !hasProblem;
	const problemMessage = setup && hasProblem ? getProblemMessage(setup) : null;
	if (isLoading && !setup) return <ToolLoadingCard title="Google Calendar" />;
	if (setup?.state === "not_available") return null;

	return (
		<CollapsibleSettingsPanel
			icon={<CalendarDays className="h-5 w-5" />}
			title={t({ id: "googleCalendar", message: "Google Calendar" })}
			description={t({
				id: "googleCalendarDescription",
				message:
					"Let selected agents offer available times and book confirmed appointments.",
			})}
			headerAction={
				setup ? (
					<Badge
						variant={
							setup.state === "configuration_error"
								? "destructive"
								: setup.state === "available_configured"
									? "default"
									: "secondary"
						}
					>
						{setup.state === "available_configured" && (
							<CheckCircle2 className="h-3 w-3" />
						)}
						{setup.state === "configuration_error" && (
							<AlertCircle className="h-3 w-3" />
						)}
						{stateLabel(setup.state)}
					</Badge>
				) : null
			}
		>
			<div className="space-y-5">
				{isLoading && !setup ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" />
						{t({
							id: "calendarConfigurationLoading",
							message: "Loading Calendar configuration...",
						})}
					</div>
				) : setup ? (
					<>
						{problemMessage && (
							<div className="flex gap-2 rounded-md border border-destructive/30 bg-danger-tint p-3 text-sm text-danger-ink">
								<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
								<span>{problemMessage}</span>
							</div>
						)}

						<div className="flex flex-wrap items-center gap-2">
							{isConnectionValid ? (
								<Button
									variant="outline"
									onClick={() => setShowDisconnectDialog(true)}
									disabled={action !== null}
								>
									{action === "disconnect" ? (
										<Loader2 className="me-2 h-4 w-4 animate-spin" />
									) : (
										<Unlink className="me-2 h-4 w-4" />
									)}
									{t({
										id: "disconnectGoogleCalendar",
										message: "Disconnect Google Calendar",
									})}
								</Button>
							) : (
								<Button
									variant="outline"
									onClick={() => void handleConnect()}
									disabled={action !== null}
								>
									{action === "connect" ? (
										<Loader2 className="me-2 h-4 w-4 animate-spin" />
									) : hasProblem ? (
										<RefreshCw className="me-2 h-4 w-4" />
									) : (
										<ExternalLink className="me-2 h-4 w-4" />
									)}
									{hasProblem
										? t({
												id: "reconnectGoogleCalendar",
												message: "Reconnect Google Calendar",
											})
										: t({
												id: "connectGoogleCalendar",
												message: "Connect Google Calendar",
											})}
								</Button>
							)}
							<Button
								variant="ghost"
								onClick={() => {
									if (oauthPollRef.current !== null) {
										window.clearInterval(oauthPollRef.current);
										oauthPollRef.current = null;
									}
									setAction(null);
									void loadSetup();
								}}
								disabled={
									isLoading || action === "save" || action === "disconnect"
								}
							>
								<RefreshCw
									className={`me-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
								/>
								{t({ id: "refresh", message: "Refresh" })}
							</Button>
						</div>

						{setup.calendars.length > 0 && (
							<>
								<div className="space-y-2">
									<Label htmlFor="google-calendar-id">
										{t({
											id: "defaultBookingCalendar",
											message: "Default booking calendar",
										})}
									</Label>
									<Select
										value={selectedCalendarId}
										onValueChange={setSelectedCalendarId}
										disabled={action !== null}
									>
										<SelectTrigger id="google-calendar-id">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{setup.calendars.map((calendar) => (
												<SelectItem key={calendar.id} value={calendar.id}>
													{calendar.summary}
													{calendar.primary
														? t({
																id: "primaryCalendarSuffix",
																message: " (primary)",
															})
														: ""}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<fieldset className="space-y-3">
									<legend className="text-sm font-medium">
										{t({
											id: "calendarEnabledAgents",
											message: "Agents with Calendar access",
										})}
									</legend>
									{agents.length === 0 ? (
										<p className="text-sm text-muted-foreground">
											{t({
												id: "calendarNoAgents",
												message:
													"Create an agent before enabling Calendar access.",
											})}
										</p>
									) : (
										<div className="divide-y rounded-md border">
											{agents.map((agent) => {
												const selected = selectedAgentIds.has(agent.id);
												const hasTimeZone = validTimeZone(agent.timeZone);
												const bindingError = setup.bindings.find(
													(binding) => binding.agentId === agent.id,
												)?.error;
												return (
													<div
														key={agent.id}
														className="flex items-start gap-3 p-3"
													>
														<Checkbox
															id={`calendar-agent-${agent.id}`}
															checked={selected}
															onCheckedChange={(checked) =>
																toggleAgent(agent.id, checked === true)
															}
															disabled={
																action !== null || (!hasTimeZone && !selected)
															}
														/>
														<div className="min-w-0 space-y-1">
															<Label htmlFor={`calendar-agent-${agent.id}`}>
																{agent.name}
															</Label>
															<p className="text-xs text-muted-foreground">
																{hasTimeZone
																	? agent.timeZone
																	: t({
																			id: "calendarAgentTimezoneMissing",
																			message: "Timezone missing or invalid",
																		})}
															</p>
															{bindingError && (
																<p className="text-xs text-danger-ink">
																	{errorMessage(bindingError)}
																</p>
															)}
														</div>
													</div>
												);
											})}
										</div>
									)}
								</fieldset>

								<div className="flex flex-wrap gap-2">
									<Button
										onClick={() => void handleSave()}
										disabled={!selectedCalendarId || action !== null}
									>
										{action === "save" && (
											<Loader2 className="me-2 h-4 w-4 animate-spin" />
										)}
										{t({
											id: "saveCalendarConfiguration",
											message: "Save Calendar configuration",
										})}
									</Button>
								</div>
							</>
						)}
					</>
				) : (
					<div className="space-y-3">
						<p role="alert" className="text-sm text-destructive">
							{t({
								id: "settings.toolLoadFailed",
								message: "Could not load tool settings. Please retry.",
							})}
						</p>
						<Button variant="outline" onClick={() => void loadSetup()}>
							<RefreshCw className="me-2 h-4 w-4" />
							{t({ id: "retry", message: "Retry" })}
						</Button>
					</div>
				)}
			</div>
			<AlertDialog
				open={showDisconnectDialog}
				onOpenChange={setShowDisconnectDialog}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t({
								id: "disconnectGoogleCalendarTitle",
								message: "Disconnect Google Calendar?",
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t({
								id: "disconnectGoogleCalendarDescription",
								message:
									"This will disconnect your Google Calendar account and disable calendar tools for all agents.",
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={action === "disconnect"}>
							{t({ id: "cancel", message: "Cancel" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault();
								void handleDisconnect();
							}}
							disabled={action === "disconnect"}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{action === "disconnect" && (
								<Loader2 className="me-2 h-4 w-4 animate-spin" />
							)}
							{t({
								id: "disconnectGoogleCalendarConfirm",
								message: "Disconnect Google Calendar",
							})}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</CollapsibleSettingsPanel>
	);
}
